// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Country from "./country.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": true
    },
    "country_id": {
      "column": {
        "default": null,
        "name": "country_id",
        "null": false,
        "type": null
      },
      "name": "country_id",
      "selected_by_default": null,
      "translated": false
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": true
    },
    "state_code": {
      "column": {
        "default": null,
        "name": "state_code",
        "null": false,
        "type": "string"
      },
      "name": "state_code",
      "selected_by_default": null,
      "translated": false
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": true
    },
    "name": {
      "column": null,
      "name": "name",
      "selected_by_default": false,
      "translated": true
    },
    "name_da": {
      "column": null,
      "name": "name_da",
      "selected_by_default": false,
      "translated": false
    },
    "name_en": {
      "column": null,
      "name": "name_en",
      "selected_by_default": false,
      "translated": false
    },
    "name_no": {
      "column": null,
      "name": "name_no",
      "selected_by_default": false,
      "translated": false
    },
    "name_se": {
      "column": null,
      "name": "name_se",
      "selected_by_default": false,
      "translated": false
    }
  },
  "className": "CountryState",
  "collectionKey": "country_states",
  "collectionName": "country_states",
  "i18nKey": "country_state",
  "camelizedLower": "countryState",
  "name": "CountryState",
  "nameDasherized": "country-state",
  "pluralName": "country_states",
  "ransackable_associations": [
    {
      "className": "CountryState::Audit",
      "collectionName": "country_state_audits",
      "foreignKey": "country_state_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "CountryStateAudit",
      "through": null
    },
    {
      "className": "Contact",
      "collectionName": "contacts",
      "foreignKey": "country_state_id",
      "name": "contacts",
      "macro": "has_many",
      "resource_name": "Contact",
      "through": null
    },
    {
      "className": "Country",
      "collectionName": "countries",
      "foreignKey": "country_id",
      "name": "country",
      "macro": "belongs_to",
      "resource_name": "Country",
      "through": null
    },
    {
      "className": "CountryState::Audit",
      "collectionName": "country_state_audits",
      "foreignKey": "country_state_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "CountryStateAudit",
      "through": null
    },
    {
      "className": "CountryState::Translation",
      "collectionName": null,
      "foreignKey": "country_state_id",
      "name": "current_translation",
      "macro": "has_one",
      "resource_name": null,
      "through": null
    },
    {
      "className": "School",
      "collectionName": "schools",
      "foreignKey": "contact_id",
      "name": "schools",
      "macro": "has_many",
      "resource_name": "School",
      "through": "contacts"
    },
    {
      "className": "CountryState::Translation",
      "collectionName": null,
      "foreignKey": "country_state_id",
      "name": "translations",
      "macro": "has_many",
      "resource_name": null,
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "country_id",
      "column": {
        "default": null,
        "name": "country_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "state_code",
      "column": {
        "default": null,
        "name": "state_code",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "Country",
      "collectionName": "countries",
      "foreignKey": "country_id",
      "name": "country",
      "macro": "belongs_to",
      "resource_name": "Country",
      "through": null
    }
  ],
  "paramKey": "country_state",
  "primaryKey": "id"
}

/** Frontend model for CountryState. */
export default class CountryState extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  countryId() {
    return this.readAttributeUnderscore("country_id")
  }

  /** @returns {boolean} */
  hasCountryId() {
    const value = this.countryId()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  stateCode() {
    return this.readAttributeUnderscore("state_code")
  }

  /** @returns {boolean} */
  hasStateCode() {
    const value = this.stateCode()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  name() {
    return this.readAttributeUnderscore("name")
  }

  /** @returns {boolean} */
  hasName() {
    const value = this.name()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameDa() {
    return this.readAttributeUnderscore("name_da")
  }

  /** @returns {boolean} */
  hasNameDa() {
    const value = this.nameDa()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameEn() {
    return this.readAttributeUnderscore("name_en")
  }

  /** @returns {boolean} */
  hasNameEn() {
    const value = this.nameEn()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameNo() {
    return this.readAttributeUnderscore("name_no")
  }

  /** @returns {boolean} */
  hasNameNo() {
    const value = this.nameNo()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameSe() {
    return this.readAttributeUnderscore("name_se")
  }

  /** @returns {boolean} */
  hasNameSe() {
    const value = this.nameSe()

    return this._isPresent(value)
  }

  /** @returns {import("./country.js").default | null} */
  country() {
    return this._readBelongsToReflection({modelClass: Country, reflectionName: "country"})
  }

  /** @returns {Promise<import("./country.js").default | null>} */
  loadCountry() {
    if (!("countryId" in this)) throw new Error("Foreign key method wasn't defined: countryId")

    const id = this.countryId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "country",
        model: this,
        modelClass: Country
      },
      {ransack}
    )
  }
}
